import { ElementType, HTMLAttributes, createContext, useContext } from "react";
import { cn } from "../../../lib/utils";

const FIGMA_TYPOGRAPHY_VARIANTS = {
  "heading-1": "font-medium text-[28px] leading-[40px] tracking-[-0.01em]",
  "heading-2": "font-medium text-[22px] leading-[32px] tracking-[-0.01em]",
  "heading-3": "font-medium text-[18px] leading-[28px] tracking-[-0.01em]",
  "heading-4": "font-medium text-[16px] leading-[25px] tracking-[-0.01em]",
  "heading-5": "font-medium text-[14px] leading-[22px] tracking-[-0.01em]",

  "label-1": "font-medium text-[16px] leading-[15px]",
  "label-2": "font-medium text-[14px] leading-[13px]",
  "label-3": "font-medium text-[12px] leading-[11px]",

  "body-1": "font-normal text-[16px] leading-[26px]",
  "body-2": "font-normal text-[14px] leading-[22px]",
  "body-2-semibold": "font-semibold text-[14px] leading-[22px]",
  "body-3": "font-normal text-[12px] leading-[18px]",

  "caption-1": "font-medium text-[12px] leading-[16px] tracking-[0.06em] uppercase",
  "caption-2": "font-medium text-[10px] leading-[16px] tracking-[0.06em] uppercase",

  "memo-heading-1": "font-semibold text-[24px] leading-[32px]",
  "memo-heading-2": "font-semibold text-[20px] leading-[26px]",
  "memo-heading-3": "font-semibold text-[18px] leading-[24px]",
  "memo-body-semibold": "font-semibold text-[15px] leading-[23px]",
  "memo-body-regular": "font-normal text-[15px] leading-[23px]",
  "memo-body-italic": "font-normal italic text-[15px] leading-[23px]",
  "memo-body-underline": "font-normal underline text-[15px] leading-[23px]"
} as const;

type TFigmaTypographyVariant = keyof typeof FIGMA_TYPOGRAPHY_VARIANTS;

type TFigmaTypographyVariants = Record<TFigmaTypographyVariant, string>;

export const REDESIGN_TYPOGRAPHY_VARIANTS: TFigmaTypographyVariants = {
  "heading-1": "typo-h3",
  "heading-2": "typo-h5",
  "heading-3": "typo-h6",
  "heading-4": "typo-label-lg",
  "heading-5": "typo-label-md",

  "label-1": "typo-label-lg",
  "label-2": "typo-label-md",
  "label-3": "typo-label-sm",

  "body-1": "typo-body-md",
  "body-2": "typo-body-sm",
  "body-2-semibold": "typo-body-sm font-semibold",
  "body-3": "typo-caption",

  "caption-1": "typo-overline",
  "caption-2": "typo-overline text-[10px]",

  "memo-heading-1": FIGMA_TYPOGRAPHY_VARIANTS["memo-heading-1"],
  "memo-heading-2": FIGMA_TYPOGRAPHY_VARIANTS["memo-heading-2"],
  "memo-heading-3": FIGMA_TYPOGRAPHY_VARIANTS["memo-heading-3"],
  "memo-body-semibold": FIGMA_TYPOGRAPHY_VARIANTS["memo-body-semibold"],
  "memo-body-regular": FIGMA_TYPOGRAPHY_VARIANTS["memo-body-regular"],
  "memo-body-italic": FIGMA_TYPOGRAPHY_VARIANTS["memo-body-italic"],
  "memo-body-underline": FIGMA_TYPOGRAPHY_VARIANTS["memo-body-underline"]
};

const FigmaTypographyVariantsContext = createContext<TFigmaTypographyVariants>(FIGMA_TYPOGRAPHY_VARIANTS);

export const FigmaTypographyVariantsProvider = FigmaTypographyVariantsContext.Provider;

const FIGMA_TYPOGRAPHY_COLORS = {
  zinc100: "text-innoveraZinc-100",
  zinc200: "text-innoveraZinc-200",
  zinc300: "text-innoveraZinc-300",
  zinc400: "text-innoveraZinc-400",
  zinc500: "text-innoveraZinc-500",
  zinc600: "text-innoveraZinc-600",
  zinc700: "text-innoveraZinc-700",
  zinc800: "text-innoveraZinc-800",
  zinc900: "text-innoveraZinc-900"
} as const;

type TFigmaTypographyColor = keyof typeof FIGMA_TYPOGRAPHY_COLORS;

type TFigmaTypographyProps = HTMLAttributes<HTMLElement> & {
  component?: ElementType;
  variant?: TFigmaTypographyVariant;
  className?: string;
  color?: TFigmaTypographyColor;
};

export function FigmaTypography(props: TFigmaTypographyProps) {
  const { component: Component = "p", variant = "body-1", className, children, color, ...rest } = props;
  const variantClass = useContext(FigmaTypographyVariantsContext)[variant];
  const colorClass = color ? FIGMA_TYPOGRAPHY_COLORS[color] : "";
  return (
    <Component className={cn(variantClass, colorClass, className)} {...rest}>
      {children}
    </Component>
  );
}
